import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { DatePicker } from "@/components/ui/date-picker";
import { Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { accountingApi } from "../services/accountingApi";
import { AccountAsyncSelect } from "../components/AccountAsyncSelect";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useNotification } from "@/shared/hooks/useNotification";
import type {
  TrialBalanceReportDto, BalanceSheetReportDto, ProfitAndLossReportDto, CashFlowReportDto,
  GeneralLedgerReportDto, CustomerLedgerSummaryDto, SupplierLedgerSummaryDto,
  SalesRegisterDto, PurchaseRegisterDto, ApSummaryDto,
  SalesInvoiceTrendsDto, PurchaseInvoiceTrendsDto,
  BalanceSheetSectionDto, ProfitAndLossSectionDto,
  ProfitAndLossLineDto,
} from "../services/accountingApi";

type ReportType =
  | "trial-balance" | "balance-sheet" | "profit-and-loss" | "cash-flow"
  | "general-ledger" | "customer-ledger" | "supplier-ledger"
  | "sales-register" | "purchase-register" | "ap-summary"
  | "sales-trends" | "purchase-trends";

const REPORT_OPTIONS: { value: ReportType; label: string; group: string; permission: string }[] = [
  { value: "trial-balance", label: "Trial Balance", group: "Financial Statements", permission: "financial_statements.view_trial_balance" },
  { value: "balance-sheet", label: "Balance Sheet", group: "Financial Statements", permission: "financial_statements.view_balance_sheet" },
  { value: "profit-and-loss", label: "Profit & Loss", group: "Financial Statements", permission: "financial_statements.view_profit_and_loss" },
  { value: "cash-flow", label: "Cash Flow Statement", group: "Financial Statements", permission: "financial_statements.view_cash_flow" },
  { value: "general-ledger", label: "General Ledger", group: "General Ledger", permission: "financial_statements.view_general_ledger" },
  { value: "customer-ledger", label: "Customer Ledger", group: "General Ledger", permission: "financial_statements.view_customer_ledger" },
  { value: "supplier-ledger", label: "Supplier Ledger", group: "General Ledger", permission: "financial_statements.view_supplier_ledger" },
  { value: "sales-register", label: "Sales Register", group: "Registers", permission: "financial_statements.view_registers" },
  { value: "purchase-register", label: "Purchase Register", group: "Registers", permission: "financial_statements.view_registers" },
  { value: "ap-summary", label: "Accounts Payable Summary", group: "Registers", permission: "financial_statements.view_registers" },
  { value: "sales-trends", label: "Sales Invoice Trends", group: "Trends", permission: "financial_statements.view_trends" },
  { value: "purchase-trends", label: "Purchase Invoice Trends", group: "Trends", permission: "financial_statements.view_trends" },
];

function fmt(n: number) {
  return new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", minimumFractionDigits: 2 }).format(n);
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-KE", { year: "numeric", month: "short", day: "numeric" });
}

// ── Sub-renderers ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <Card className={className}>
      <CardContent className="pt-4 pb-3">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

function TrialBalanceView({ data }: { data: TrialBalanceReportDto }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="As Of" value={fmtDate(data.asOfDate)} />
        <MetricCard label="Total Debits" value={fmt(data.totalDebits)} />
        <MetricCard label="Total Credits" value={fmt(data.totalCredits)} />
        <MetricCard label="Balanced" value={data.isBalanced ? "Yes" : "No"} className={data.isBalanced ? "" : "border-red-300"} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead>Type</TableHead>
            <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.accountId}>
              <TableCell className="font-mono text-sm">{r.accountCode}</TableCell>
              <TableCell>{r.accountName}</TableCell>
              <TableCell><Badge variant="outline">{r.accountTypeName}</Badge></TableCell>
              <TableCell className="text-right font-mono">{r.debitBalance > 0 ? fmt(r.debitBalance) : "—"}</TableCell>
              <TableCell className="text-right font-mono">{r.creditBalance > 0 ? fmt(r.creditBalance) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BalanceSheetSectionView({ section }: { section: BalanceSheetSectionDto }) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-lg">{section.sectionName} — {fmt(section.total)}</h3>
      {section.subSections.map((sub) => (
        <div key={sub.accountId} className="ml-4">
          <p className="font-medium text-sm">{sub.accountCode} {sub.accountName} — {fmt(sub.total)}</p>
          {sub.lines.map((l) => (
            <div key={l.accountId} className="ml-4 flex justify-between text-sm">
              <span>{l.accountCode} {l.accountName}</span><span className="font-mono">{fmt(l.balance)}</span>
            </div>
          ))}
        </div>
      ))}
      {section.lines.filter((l) => !section.subSections.some((s) => s.lines.some((sl) => sl.accountId === l.accountId))).map((l) => (
        <div key={l.accountId} className="ml-4 flex justify-between text-sm">
          <span>{l.accountCode} {l.accountName}</span><span className="font-mono">{fmt(l.balance)}</span>
        </div>
      ))}
    </div>
  );
}

function BalanceSheetView({ data }: { data: BalanceSheetReportDto }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard label="Total Assets" value={fmt(data.totalAssets)} />
        <MetricCard label="Total Liabilities" value={fmt(data.totalLiabilities)} />
        <MetricCard label="Total Equity" value={fmt(data.totalEquity)} />
        <MetricCard label="Retained Earnings" value={fmt(data.retainedEarnings)} />
        <MetricCard label="Balanced" value={data.isBalanced ? "Yes" : "No"} className={data.isBalanced ? "" : "border-red-300"} />
      </div>
      <BalanceSheetSectionView section={data.assets} />
      <BalanceSheetSectionView section={data.liabilities} />
      <BalanceSheetSectionView section={data.equity} />
    </div>
  );
}

function PnLLineView({ line, depth = 1 }: { line: ProfitAndLossLineDto; depth?: number }) {
  const children = line.children ?? [];
  const hasChildren = children.length > 0;

  return (
    <div className="space-y-1" style={{ marginLeft: `${depth}rem` }}>
      <div className={`flex justify-between gap-3 text-sm ${hasChildren ? "font-medium" : ""}`}>
        <span>{line.accountCode} {line.accountName}</span>
        <span className="font-mono">{fmt(line.amount)}</span>
      </div>
      {children.map((child) => (
        <PnLLineView key={child.accountId} line={child} depth={depth + 1} />
      ))}
    </div>
  );
}

function PnLSectionView({ section }: { section: ProfitAndLossSectionDto }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-lg">{section.sectionName}</h3>
        <span className="font-mono text-base font-semibold">{fmt(section.total)}</span>
      </div>
      <div className="space-y-1">
        {section.lines.map((line) => (
          <PnLLineView key={line.accountId} line={line} />
        ))}
        {section.lines.length === 0 && (
          <div className="ml-4 flex justify-between text-sm text-muted-foreground">
            <span>No activity</span><span className="font-mono">{fmt(0)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfitAndLossView({ data }: { data: ProfitAndLossReportDto }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Total Income" value={fmt(data.income.total)} />
        <MetricCard label="Total Expenses" value={fmt(data.expenses.total)} />
        <MetricCard label="Net Profit" value={fmt(data.netProfit)} className={data.netProfit >= 0 ? "" : "border-red-300"} />
        <MetricCard label="Margin" value={`${data.netProfitMarginPercent.toFixed(1)}%`} />
      </div>
      <PnLSectionView section={data.income} />
      <PnLSectionView section={data.expenses} />
    </div>
  );
}

function CashFlowView({ data }: { data: CashFlowReportDto }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Opening Cash" value={fmt(data.openingCashBalance)} />
        <MetricCard label="Net Cash Change" value={fmt(data.netCashChange)} />
        <MetricCard label="Closing Cash" value={fmt(data.closingCashBalance)} />
      </div>
      {[data.operatingActivities, data.investingActivities, data.financingActivities].map((section) => (
        <Card key={section.sectionName}>
          <CardHeader className="pb-2"><CardTitle className="text-base">{section.sectionName} — {fmt(section.netAmount)}</CardTitle></CardHeader>
          <CardContent>
            {section.lines.map((l, i) => (
              <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                <span>{l.description}</span><span className="font-mono">{fmt(l.amount)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function GeneralLedgerView({ data }: { data: GeneralLedgerReportDto }) {
  const isAccountLedger = !!data.accountId;

  return (
    <div className="space-y-4">
      {isAccountLedger && (
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-sm text-muted-foreground">Account Ledger</div>
            <div className="text-lg font-semibold">
              {data.accountCode} {data.accountName}
            </div>
            <div className="text-sm text-muted-foreground">{data.accountTypeName ?? ""}</div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Opening Balance" value={fmt(data.openingBalance)} />
        <MetricCard label="Total Debits" value={fmt(data.totalDebits)} />
        <MetricCard label="Total Credits" value={fmt(data.totalCredits)} />
        <MetricCard label="Closing Balance" value={fmt(data.closingBalance)} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead><TableHead>Entry #</TableHead><TableHead>Source / Reference</TableHead>
            {!isAccountLedger && <TableHead>Account</TableHead>}
            <TableHead>Narration</TableHead><TableHead className="text-right">Debit</TableHead>
            <TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Running Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.entries.map((e, i) => (
            <TableRow key={i}>
              <TableCell className="text-sm">{fmtDate(e.postingDate)}</TableCell>
              <TableCell className="font-mono text-sm">{e.journalEntryNumber}</TableCell>
              <TableCell className="text-sm">
                <div className="flex flex-col">
                  <span>{e.sourceModule || "Manual"}</span>
                  <span className="font-mono text-xs text-muted-foreground">{e.sourceDocumentNumber || e.referenceNumber || "—"}</span>
                </div>
              </TableCell>
              {!isAccountLedger && <TableCell className="text-sm">{e.accountCode} {e.accountName}</TableCell>}
              <TableCell className="text-sm max-w-[150px] truncate">{e.narration || "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm">{e.debitAmount > 0 ? fmt(e.debitAmount) : "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm">{e.creditAmount > 0 ? fmt(e.creditAmount) : "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(e.runningBalance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CustomerLedgerView({ data }: { data: CustomerLedgerSummaryDto }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard label="Total Invoiced" value={fmt(data.totalInvoiced)} />
        <MetricCard label="Total Paid" value={fmt(data.totalPaid)} />
        <MetricCard label="Outstanding" value={fmt(data.totalOutstanding)} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead><TableHead className="text-right">Opening</TableHead>
            <TableHead className="text-right">Invoiced</TableHead><TableHead className="text-right">Payments</TableHead>
            <TableHead className="text-right">Credit Notes</TableHead><TableHead className="text-right">Closing</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.customers.map((c) => (
            <TableRow key={c.customerId}>
              <TableCell>{c.customerName}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(c.openingBalance)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(c.invoiced)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(c.payments)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(c.creditNotes)}</TableCell>
              <TableCell className="text-right font-mono text-sm font-semibold">{fmt(c.closingBalance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SupplierLedgerView({ data }: { data: SupplierLedgerSummaryDto }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard label="Total Purchased" value={fmt(data.totalPurchased)} />
        <MetricCard label="Total Paid" value={fmt(data.totalPaid)} />
        <MetricCard label="Outstanding" value={fmt(data.totalOutstanding)} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Supplier</TableHead><TableHead className="text-right">Opening</TableHead>
            <TableHead className="text-right">Purchases</TableHead><TableHead className="text-right">Payments</TableHead>
            <TableHead className="text-right">Closing</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.suppliers.map((s) => (
            <TableRow key={s.supplierId}>
              <TableCell>{s.supplierName}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(s.openingBalance)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(s.purchases)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(s.payments)}</TableCell>
              <TableCell className="text-right font-mono text-sm font-semibold">{fmt(s.closingBalance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SalesRegisterView({ data }: { data: SalesRegisterDto }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Invoices" value={String(data.invoiceCount)} />
        <MetricCard label="Total Invoiced" value={fmt(data.totalInvoiced)} />
        <MetricCard label="Total Tax" value={fmt(data.totalTax)} />
        <MetricCard label="Net" value={fmt(data.totalNet)} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice #</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.invoices.map((inv) => (
            <TableRow key={inv.invoiceId}>
              <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
              <TableCell className="text-sm">{fmtDate(inv.invoiceDate)}</TableCell>
              <TableCell className="text-sm">{inv.customerName}</TableCell>
              <TableCell><Badge variant="outline">{inv.status}</Badge></TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.totalAmount)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.paidAmount)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.balanceAmount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PurchaseRegisterView({ data }: { data: PurchaseRegisterDto }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Invoices" value={String(data.invoiceCount)} />
        <MetricCard label="Total Purchased" value={fmt(data.totalPurchased)} />
        <MetricCard label="Total VAT" value={fmt(data.totalVat)} />
        <MetricCard label="Net" value={fmt(data.totalNet)} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice #</TableHead><TableHead>Date</TableHead><TableHead>Supplier</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.invoices.map((inv) => (
            <TableRow key={inv.invoiceId}>
              <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
              <TableCell className="text-sm">{fmtDate(inv.invoiceDate)}</TableCell>
              <TableCell className="text-sm">{inv.supplierName}</TableCell>
              <TableCell><Badge variant="outline">{inv.status}</Badge></TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.totalAmount)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.paidAmount)}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(inv.balanceAmount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ApSummaryView({ data }: { data: ApSummaryDto }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Outstanding" value={fmt(data.totalOutstanding)} />
        <MetricCard label="Outstanding Invoices" value={String(data.outstandingInvoiceCount)} />
        <MetricCard label="Overdue" value={fmt(data.totalOverdue)} className="border-red-300" />
        <MetricCard label="Overdue Invoices" value={String(data.overdueInvoiceCount)} />
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Aging Buckets</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Bucket</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Invoices</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.agingBuckets.map((b) => (
                <TableRow key={b.label}><TableCell>{b.label}</TableCell><TableCell className="text-right font-mono">{fmt(b.amount)}</TableCell><TableCell className="text-right">{b.invoiceCount}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Top Creditors</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Supplier</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Overdue</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.topCreditors.map((c) => (
                <TableRow key={c.supplierId}><TableCell>{c.supplierName}</TableCell><TableCell className="text-right font-mono">{fmt(c.outstanding)}</TableCell><TableCell className="text-right font-mono">{fmt(c.overdue)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function TrendsView({ data }: { data: SalesInvoiceTrendsDto | PurchaseInvoiceTrendsDto }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Period</TableHead><TableHead className="text-right">Invoices</TableHead>
          <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Average</TableHead>
          <TableHead className="text-right">Growth</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.periods.map((p) => (
          <TableRow key={p.period}>
            <TableCell>{p.period}</TableCell>
            <TableCell className="text-right">{p.invoiceCount}</TableCell>
            <TableCell className="text-right font-mono">{fmt(p.totalAmount)}</TableCell>
            <TableCell className="text-right font-mono">{fmt(p.averageInvoiceValue)}</TableCell>
            <TableCell className="text-right">
              <span className={p.growthPercent >= 0 ? "text-green-600" : "text-red-600"}>{p.growthPercent.toFixed(1)}%</span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function FinancialReportsPage() {
  const { hasPermission } = useAuthStore();
  const notify = useNotification();
  const allowedReports = useMemo(
    () => REPORT_OPTIONS.filter((o) => hasPermission(o.permission)),
    [hasPermission]
  );
  const [reportType, setReportType] = useState<ReportType>(allowedReports[0]?.value ?? "trial-balance");
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [glAccountId, setGlAccountId] = useState<string | undefined>(undefined);
  const [glSourceModule, setGlSourceModule] = useState<string | undefined>(undefined);
  const [glSearchText, setGlSearchText] = useState("");
  const [glPageNumber, setGlPageNumber] = useState(1);
  const [glPageSize, setGlPageSize] = useState(50);
  const [runToken, setRunToken] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const needsDateRange = !["trial-balance", "balance-sheet", "ap-summary"].includes(reportType);
  const isGeneralLedgerReport = reportType === "general-ledger";

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "financial-report",
      reportType,
      asOfDate,
      startDate,
      endDate,
      glAccountId ?? null,
      glSourceModule ?? null,
      glSearchText,
      glPageNumber,
      glPageSize,
      runToken,
    ],
    queryFn: async () => {
      switch (reportType) {
        case "trial-balance": return accountingApi.getTrialBalance(asOfDate);
        case "balance-sheet": return accountingApi.getBalanceSheet(asOfDate);
        case "profit-and-loss": return accountingApi.getProfitAndLoss(startDate, endDate);
        case "cash-flow": return accountingApi.getCashFlow(startDate, endDate);
        case "general-ledger":
          return accountingApi.getGeneralLedger(
            startDate,
            endDate,
            glAccountId,
            undefined,
            glSourceModule,
            glSearchText || undefined,
              glPageNumber,
              glPageSize,
          );
        case "customer-ledger": return accountingApi.getCustomerLedger(startDate, endDate);
        case "supplier-ledger": return accountingApi.getSupplierLedger(startDate, endDate);
        case "sales-register": return accountingApi.getSalesRegister(startDate, endDate);
        case "purchase-register": return accountingApi.getPurchaseRegister(startDate, endDate);
        case "ap-summary": return accountingApi.getApSummary(asOfDate);
        case "sales-trends": return accountingApi.getSalesInvoiceTrends(startDate, endDate);
        case "purchase-trends": return accountingApi.getPurchaseInvoiceTrends(startDate, endDate);
      }
    },
    enabled: runToken > 0,
  });

  const renderReport = () => {
    if (isLoading) return <div className="text-center py-12 text-muted-foreground">Loading report...</div>;
    if (error) return <div className="text-center py-12 text-red-500">Error loading report. Please try again.</div>;
    if (!data) return <div className="text-center py-12 text-muted-foreground">Select a report and click "Run Report" to begin.</div>;

    switch (reportType) {
      case "trial-balance": return <TrialBalanceView data={data as TrialBalanceReportDto} />;
      case "balance-sheet": return <BalanceSheetView data={data as BalanceSheetReportDto} />;
      case "profit-and-loss": return <ProfitAndLossView data={data as ProfitAndLossReportDto} />;
      case "cash-flow": return <CashFlowView data={data as CashFlowReportDto} />;
      case "general-ledger": return <GeneralLedgerView data={data as GeneralLedgerReportDto} />;
      case "customer-ledger": return <CustomerLedgerView data={data as CustomerLedgerSummaryDto} />;
      case "supplier-ledger": return <SupplierLedgerView data={data as SupplierLedgerSummaryDto} />;
      case "sales-register": return <SalesRegisterView data={data as SalesRegisterDto} />;
      case "purchase-register": return <PurchaseRegisterView data={data as PurchaseRegisterDto} />;
      case "ap-summary": return <ApSummaryView data={data as ApSummaryDto} />;
      case "sales-trends": return <TrendsView data={data as SalesInvoiceTrendsDto} />;
      case "purchase-trends": return <TrendsView data={data as PurchaseInvoiceTrendsDto} />;
      default: return null;
    }
  };

  const generalLedgerData = reportType === "general-ledger" ? (data as GeneralLedgerReportDto | undefined) : undefined;
  const canGoNextPage = !!generalLedgerData && generalLedgerData.entries.length === glPageSize;
  const canExport = ["trial-balance", "balance-sheet", "profit-and-loss", "general-ledger"].includes(reportType) && runToken > 0;

  const downloadFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleExport = async (format: "xlsx" | "pdf") => {
    if (!canExport) {
      notify.warning("Run a supported report before exporting");
      return;
    }

    setIsExporting(true);
    try {
      let file;
      switch (reportType) {
        case "trial-balance":
          file = await accountingApi.exportTrialBalance({ asOfDate, format });
          break;
        case "balance-sheet":
          file = await accountingApi.exportBalanceSheet({ asOfDate, format });
          break;
        case "profit-and-loss":
          file = await accountingApi.exportProfitAndLoss({ startDate, endDate, format });
          break;
        case "general-ledger":
          file = await accountingApi.exportGeneralLedger({
            startDate,
            endDate,
            accountId: glAccountId,
            sourceModule: glSourceModule,
            searchText: glSearchText || undefined,
            format,
          });
          break;
        default:
          notify.warning("Export is not available for this report yet");
          return;
      }

      downloadFile(file.blob, file.fileName);
      notify.success(`Exported ${file.fileName}`);
    } catch {
      notify.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Financial Reports</h1>
        <p className="text-muted-foreground">Financial statements, ledgers, registers, and trend reports</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-64">
              <label className="text-sm font-medium mb-1 block">Report Type</label>
              <Select value={reportType} onValueChange={(v) => { setReportType(v as ReportType); setGlPageNumber(1); setRunToken(0); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedReports.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!needsDateRange ? (
              <div>
                <label className="text-sm font-medium mb-1 block">As Of Date</label>
                <DatePicker id="asOfDate" value={asOfDate} onChange={(v) => { if (v) { setAsOfDate(v); setGlPageNumber(1); } }} />
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium mb-1 block">Date Range</label>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={({ startDate: s, endDate: e }) => {
                    if (s) setStartDate(s);
                    if (e) setEndDate(e);
                    setGlPageNumber(1);
                  }}
                />
              </div>
            )}

            <Button onClick={() => setRunToken((t) => t + 1)} disabled={isLoading}>
              {isLoading ? "Loading..." : "Run Report"}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={isExporting || !canExport}>
                  <Download className="h-4 w-4 mr-2" />
                  {isExporting ? "Exporting..." : "Export"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("xlsx")}>Export Excel (.xlsx)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>Export PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isGeneralLedgerReport && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Account (optional)</label>
                <AccountAsyncSelect
                  valueId={glAccountId}
                  postingOnly={true}
                  activeOnly={true}
                  pageSize={20}
                  includeAllOption={true}
                  onChange={(accountId) => {
                    setGlPageNumber(1);
                    setGlAccountId(accountId);
                  }}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Source Module (optional)</label>
                <Select value={glSourceModule ?? "__ALL__"} onValueChange={(v) => {
                  setGlPageNumber(1);
                  setGlSourceModule(v === "__ALL__" ? undefined : v);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__ALL__">All sources</SelectItem>
                    <SelectItem value="SalesInvoice">Sales Invoice</SelectItem>
                    <SelectItem value="Payment">Payment</SelectItem>
                    <SelectItem value="SupplierInvoice">Supplier Invoice</SelectItem>
                    <SelectItem value="Reversal">Reversal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Search text (optional)</label>
                <Input
                  value={glSearchText}
                  onChange={(e) => {
                    setGlPageNumber(1);
                    setGlSearchText(e.target.value);
                  }}
                  placeholder="Entry #, narration, reference, doc #"
                />
              </div>
            </div>
          )}

          {isGeneralLedgerReport && (
            <div className="mt-3 flex flex-wrap items-end gap-4">
              <div className="w-40">
                <label className="text-sm font-medium mb-1 block">Page Size</label>
                <Select
                  value={String(glPageSize)}
                  onValueChange={(v) => {
                    setGlPageSize(Number(v));
                    setGlPageNumber(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="text-sm text-muted-foreground pb-2">Page {glPageNumber}</div>

              <Button
                variant="outline"
                onClick={() => setGlPageNumber((p) => Math.max(1, p - 1))}
                disabled={isLoading || glPageNumber <= 1}
              >
                Previous Page
              </Button>
              <Button
                variant="outline"
                onClick={() => setGlPageNumber((p) => p + 1)}
                disabled={isLoading || !canGoNextPage}
              >
                Next Page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>
            {reportType === "general-ledger" && !!glAccountId
              ? "Account Ledger"
              : REPORT_OPTIONS.find((o) => o.value === reportType)?.label || "Report"}
          </CardTitle>
        </CardHeader>
        <CardContent>{renderReport()}</CardContent>
      </Card>
    </div>
  );
}
